/**
 * i18n bootstrap (Phase 1).
 *
 * - 7 languages: en (default), hi, ta, te, bn, mr, pa
 * - Translations live in /src/i18n/locales/{code}.json
 * - Missing keys gracefully fall back to English so partial translations
 *   never break the UI. Phase 1 ships the high-frequency keys; adding new
 *   keys is a JSON edit — no code changes needed.
 * - Reads the saved language from localStorage on boot so the user lands
 *   in their preferred language instantly. AppContext later overrides
 *   from the server-side preference once the user is authenticated.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import api from '../services/api';

import en from './locales/en.json';
import hi from './locales/hi.json';
import ta from './locales/ta.json';
import te from './locales/te.json';
import bn from './locales/bn.json';
import mr from './locales/mr.json';
import pa from './locales/pa.json';

export const SUPPORTED_LANGUAGES = {
  en: 'English',
  hi: 'हिन्दी (Hindi)',
  ta: 'தமிழ் (Tamil)',
  te: 'తెలుగు (Telugu)',
  bn: 'বাংলা (Bengali)',
  mr: 'मराठी (Marathi)',
  pa: 'ਪੰਜਾਬੀ (Punjabi)',
};

// Per-user cache key in localStorage. We DO NOT use a shared key — each
// user's language preference is scoped to their userId so two different
// accounts on the same device never bleed languages between sessions.
function _userCacheKey(userId) {
  return `sip_platform_lang_user_${userId || 'anon'}`;
}

// On boot we don't know who the user is yet, so we use neutral English.
// AppContext calls `syncLanguageFromServer()` immediately after the auth
// state is hydrated so the user's preference is applied before the first
// protected page renders.
const initialLang = 'en';

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      hi: { translation: hi },
      ta: { translation: ta },
      te: { translation: te },
      bn: { translation: bn },
      mr: { translation: mr },
      pa: { translation: pa },
    },
    lng: initialLang,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

export function setPlatformLanguage(code, userId) {
  const lang = SUPPORTED_LANGUAGES[code] ? code : 'en';
  try {
    if (userId) localStorage.setItem(_userCacheKey(userId), lang);
  } catch { /* ignore */ }
  return i18n.changeLanguage(lang);
}

/**
 * Apply a user's saved language preference. Tries the per-user localStorage
 * cache first (fast, offline), then refreshes from the server (source of
 * truth). Falls back to English on any error.
 */
export async function syncLanguageFromServer(userId) {
  // Step 1 — instantly apply the per-user cached language so the UI doesn't
  // flash English-then-localised on every reload.
  try {
    const cached = localStorage.getItem(_userCacheKey(userId));
    if (cached && SUPPORTED_LANGUAGES[cached]) {
      await i18n.changeLanguage(cached);
    } else {
      await i18n.changeLanguage('en');
    }
  } catch { /* ignore */ }

  // Step 2 — refresh from the server (authoritative). Silently swallow
  // failures (offline / 404 if feature is disabled) and keep whatever
  // the cache gave us.
  try {
    const res = await api.getMyPreferences();
    const serverLang = res?.preferences?.platformLanguage;
    if (serverLang && SUPPORTED_LANGUAGES[serverLang]) {
      try { localStorage.setItem(_userCacheKey(userId), serverLang); } catch { /* ignore */ }
      await i18n.changeLanguage(serverLang);
    }
  } catch { /* ignore — feature disabled or transient */ }
}

/**
 * Reset i18n on logout. We DO NOT delete the per-user cache — keeping it
 * means the next time *this* user logs back in on the same device they
 * instantly see their preferred language even before the server fetch.
 * Only the active runtime language is reverted to English so the
 * unauthenticated landing/login pages stay in the neutral default.
 */
export function resetLanguageOnLogout() {
  return i18n.changeLanguage('en');
}

export default i18n;
